import { createApiHeaders } from "@/shared/api/client";

export type AltchaOperation = "thread" | "reply";

export type AltchaCredential = {
  kind: "fresh";
  altcha: string;
  token: string;
};

type AltchaChallengeControllerOptions = {
  getToken: () => Promise<string>;
  fetchImpl?: typeof fetch;
};

export type AltchaChallengeController = {
  fetch: typeof fetch;
  getCredential: (payload: string) => AltchaCredential;
  reset: () => void;
  dispose: () => void;
};

type ResolveAltchaCredentialOptions = {
  freshPayload: string | null;
  getFreshCredential: (payload: string) => AltchaCredential;
  signal?: AbortSignal;
  verify: () => Promise<string>;
};

/**
 * ALTCHA credential 解決: 既に verify 済みの payload があればそれを使い、
 * 無ければ widget.verify() で fresh payload を作って返す。
 * セッション制は廃止したため、1 投稿 1 fresh PoW で常に走る。
 */
export async function resolveAltchaCredential({
  freshPayload,
  getFreshCredential,
  signal,
  verify,
}: ResolveAltchaCredentialOptions): Promise<AltchaCredential> {
  signal?.throwIfAborted();
  if (freshPayload) return getFreshCredential(freshPayload);
  const verifiedPayload = await verify();
  signal?.throwIfAborted();
  return getFreshCredential(verifiedPayload);
}

export function getAltchaChallengePath(
  operation: AltchaOperation,
  apiBase: string = "",
): string {
  return `${apiBase}/altcha?op=${operation}`;
}

export function createAltchaChallengeController({
  getToken,
  fetchImpl = globalThis.fetch,
}: AltchaChallengeControllerOptions): AltchaChallengeController {
  let lifecycleController = new AbortController();
  let currentToken: string | null = null;
  let isDisposed = false;

  const fetchChallenge: typeof fetch = async (
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1],
  ) => {
    if (isDisposed) {
      throw new DOMException("ALTCHA widget was disposed", "AbortError");
    }

    const activeController = lifecycleController;
    const token = await getToken();
    activeController.signal.throwIfAborted();
    currentToken = token;

    const headers = createApiHeaders(init?.headers);
    headers.set("x-aimg-token", token);
    const signal = init?.signal
      ? AbortSignal.any([activeController.signal, init.signal])
      : activeController.signal;

    return fetchImpl(input, { ...init, headers, signal });
  };

  function reset(): void {
    lifecycleController.abort();
    lifecycleController = new AbortController();
    currentToken = null;
  }

  function dispose(): void {
    isDisposed = true;
    lifecycleController.abort();
    currentToken = null;
  }

  function getCredential(payload: string): AltchaCredential {
    if (!payload || !currentToken || isDisposed) {
      throw new Error("書き込み認証をもう一度確認してください");
    }
    return { kind: "fresh", altcha: payload, token: currentToken };
  }

  return { fetch: fetchChallenge, getCredential, reset, dispose };
}
